// services/activity.js — the browser half of the active-work log (docs/ACTIVITY_HUD_PLAN.md, step 1).
//
// Notices REAL input (typing, clicking, drawing, scrolling, dragging) in a visible tab, and about
// once a minute tells api/activity.js how many 15-second slices of the last minute had input and
// what the person was working on. An open, untouched tab sends nothing.
//
// What "working on" means comes from a context provider: a tool can register one
// (setActivityContextProvider) — the outline does, from its zoom state. Otherwise it's read off the
// route and its query string (?projectId=, ?milestoneId=, ...), which already covers AlderCAD,
// AlderQC, AlderSPEC and the document tools.
//
// Sessions Claude drives are marked automated: navigator.webdriver, or the 'aj-automated' flag in
// sessionStorage/localStorage (Claude sets it before driving a tab). They're kept, labelled, and
// excluded downstream.
//
// While recording is switched off server-side, the server answers { recording:false } and this
// backs off for 15 minutes between checks — no writes happen until it's turned on.

const SLICE_MS = 15000
const BEAT_MS = 60000
const OFF_BACKOFF_MS = 15 * 60 * 1000

let started = false
let getTokenFn = null
let providerFn = null
let slices = new Set()
let pausedUntil = 0
const tabKey = (() => {
  try {
    const k = sessionStorage.getItem('aj-activity-tab')
    if (k) return k
    const n = 't' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36)
    sessionStorage.setItem('aj-activity-tab', n)
    return n
  } catch { return 't' + Math.random().toString(36).slice(2, 12) }
})()

function isAutomated() {
  try {
    if (navigator.webdriver) return true
    if (sessionStorage.getItem('aj-automated') === '1') return true
    if (localStorage.getItem('aj-automated') === '1') return true
  } catch { /* storage blocked */ }
  return false
}

function noteInput() {
  if (document.visibilityState !== 'visible') return
  slices.add(Math.floor(Date.now() / SLICE_MS))
}
function onPointerMove(e) { if (e.buttons) noteInput() }   // dragging/orbiting counts; hovering doesn't

// Default context from the route — covers every standalone tool that carries its ids in the URL.
function routeContext() {
  const path = window.location.pathname.replace(/^\/+/, '').split('/')[0] || 'outline'
  const q = new URLSearchParams(window.location.search)
  const tool = ({ project: 'outline', cad: 'modeler' })[path] || path
  let projectId = q.get('projectId') || q.get('recordId') || null
  if (path === 'cad' || q.get('sandbox') != null) projectId = 'sandbox'
  return {
    tool,
    projectId,
    milestoneId: q.get('milestoneId') || q.get('msId') || null,
    entityType: q.get('sheetId') ? 'sheet' : (q.get('documentId') ? 'document' : null),
    entityId: q.get('sheetId') || q.get('documentId') || null,
  }
}

function currentContext() {
  if (providerFn) {
    try { const c = providerFn(); if (c && c.tool) return c } catch { /* fall back to the route */ }
  }
  return routeContext()
}

async function sendBeat() {
  const now = Date.now()
  const nowSlice = Math.floor(now / SLICE_MS)
  // Count slices in the last minute (plus a little slack), then forget them.
  let n = 0
  for (const s of slices) if (s > nowSlice - Math.ceil(BEAT_MS / SLICE_MS) - 2) n++
  slices = new Set()
  if (!n || now < pausedUntil || !getTokenFn) return

  const ctx = currentContext()
  let token
  try { token = await getTokenFn() } catch { return }
  if (!token) return
  try {
    const r = await fetch('/api/activity', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({
        action: 'beat',
        tool: ctx.tool,
        projectId: ctx.projectId || null,
        milestoneId: ctx.milestoneId || null,
        entityType: ctx.entityType || null,
        entityId: ctx.entityId || null,
        memberId: ctx.memberId || null,
        tabKey,
        activeSeconds: Math.min(n * (SLICE_MS / 1000), 90),
        automated: isAutomated(),
      }),
    })
    if (!r.ok) { pausedUntil = Date.now() + 5 * 60 * 1000; return }
    const j = await r.json()
    if (j && j.recording === false) pausedUntil = Date.now() + OFF_BACKOFF_MS
  } catch { pausedUntil = Date.now() + 5 * 60 * 1000 }
}

// Called once, from the signed-in app shell. getToken is Clerk's useAuth().getToken.
export function startActivityTracking(getToken) {
  getTokenFn = getToken
  if (started) return
  started = true
  const opts = { capture: true, passive: true }
  window.addEventListener('pointerdown', noteInput, opts)
  window.addEventListener('keydown', noteInput, opts)
  window.addEventListener('wheel', noteInput, opts)
  window.addEventListener('touchstart', noteInput, opts)
  window.addEventListener('pointermove', onPointerMove, opts)
  setInterval(sendBeat, BEAT_MS)
  // Flush the partial minute when the tab is hidden, so switching tabs doesn't lose it.
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') sendBeat() })
}

// A tool that knows better than its URL what the person is working on registers this.
// fn() returns { tool, projectId?, milestoneId?, entityType?, entityId?, memberId? }. Pass null to clear.
export function setActivityContextProvider(fn) { providerFn = typeof fn === 'function' ? fn : null }

async function authedFetch(url, init = {}) {
  const token = window.__clerkGetToken ? await window.__clerkGetToken() : (getTokenFn ? await getTokenFn() : null)
  const r = await fetch(url, { ...init, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token, ...(init.headers || {}) } })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status))
  return j
}

// ── Suggested time + my week (the person's own data) ──
export function getSuggestions(date, sources) {
  return authedFetch('/api/activity?action=suggestions&date=' + encodeURIComponent(date) + (sources && sources.length ? '&sources=' + sources.join(',') : ''))
}
export function markSuggestion(day, key, status, timeEntryId, covered) {
  return authedFetch('/api/activity', { method: 'POST', body: JSON.stringify({ action: 'markSuggestion', day, key, status, timeEntryId: timeEntryId || null, covered: covered || null }) })
}
export function getMyWeek() { return authedFetch('/api/activity?action=myWeek') }

// ── Google Calendar connection (per person, read-only) ──
export function getGoogleCalendarStatus() { return authedFetch('/api/activity?action=googleStatus') }
export function getGoogleCalendarConnectUrl(kind) { return authedFetch('/api/activity?action=googleConnect' + (kind ? '&kind=' + encodeURIComponent(kind) : '')) }
export function disconnectGoogleCalendar(kind) { return authedFetch('/api/activity', { method: 'POST', body: JSON.stringify({ action: 'googleDisconnect', kind: kind || 'calendar' }) }) }

// ── The Tower's project board (tower seats) ──
export function getProjectBoard() { return authedFetch('/api/activity?action=projectBoard') }
export function setProductionTool(projectId, tool) {
  return authedFetch('/api/activity', { method: 'POST', body: JSON.stringify({ action: 'setProductionTool', projectId, tool }) })
}

// Time Review: each person's engaged time in Cortex per project per day (reviewers only).
export function getReviewContext(memberIds, from, to) {
  return authedFetch('/api/activity', { method: 'POST', body: JSON.stringify({ action: 'reviewContext', memberIds, from, to }) })
}

// Admin: is recording on? / turn it on or off.
export function getActivityRecordingStatus() { return authedFetch('/api/activity?action=status') }
export function setActivityRecording(enabled) {
  return authedFetch('/api/activity', { method: 'POST', body: JSON.stringify({ action: 'setEnabled', enabled: !!enabled }) })
}
